<template>
  <div class="measurement-card" :class="{ fullscreen }">
    <CardInteractive :title="title" :info="info" titleType="label" :isLoading="isLoading">
      <div class="value">
        <TextBase
          v-if="value.type === 'string'"
          :text="value.string"
          :type="value.textType ?? 'h3'"
          :color="color"
          weight="bold"
        />
        <TextBase
          v-else-if="value.type === 'number'"
          :text="formatter(value.number, locale, value.format)"
          :type="value.textType ?? 'h3'"
          :color="color"
          weight="bold"
        />
        <GaugeBase
          v-else-if="value.type === 'gauge'"
          :value="value.number"
          :min="value.min"
          :max="value.max"
          :format="value.format"
          :color="color"
        />
        <component :is="value.icon" v-else :style="{ color }" />
      </div>
      <template #footer>
        <div class="footer-container">
          <div class="button-group">
            <ShowOnMapButton
              v-if="showOnMapEnabled"
              :active="showOnMap"
              @toggleShowOnMap="toggleShowOnMap"
            />
            <AccordionExpander
              v-if="$slots.details"
              :text="t('dashboard__text__show-details')"
              :textOpen="t('dashboard__text__hide-details')"
              size="extrasmall"
              :active="showDetails"
              @onClicked="onAccordionClicked"
            />
          </div>
          <slot v-if="showDetails" name="details"></slot>
        </div>
      </template>
    </CardInteractive>
  </div>
</template>

<script lang="ts" setup>
import TextBase, { TextType } from '@/design-system/atoms/TextBase.vue'
import CardInteractive from './CardInteractive.vue'
import AccordionExpander from '@/design-system/atoms/AccordionExpander.vue'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import ShowOnMapButton from './ShowOnMapButton.vue'
import { FormatOptions, formatter } from '../formatter'
import GaugeBase from '../../GaugeBase.vue'

export type Value =
  | { type: 'number'; number: number; textType?: TextType; format?: FormatOptions }
  | {
      type: 'gauge'
      number: number
      min?: number
      max?: number
      format?: FormatOptions
    }
  | { type: 'string'; string: string; textType?: TextType }
  | { type: 'icon'; icon: string }

export interface Props {
  title: string
  info?: string
  value?: Value
  color?: string
  showOnMapEnabled?: boolean
  showOnMap: boolean
  isLoading?: boolean
  fullscreen?: boolean
}

type Emits = {
  (e: 'toggleShowOnMap'): void
}

const { t, locale } = useI18n()

withDefaults(defineProps<Props>(), {
  showOnMapEnabled: true,
  value: () => ({ type: 'string', string: '-' }),
  color: 'red',
  info: undefined,
  table: undefined
})

const emits = defineEmits<Emits>()

const showDetails = ref(false)

function toggleShowOnMap() {
  emits('toggleShowOnMap')
}

function onAccordionClicked() {
  showDetails.value = !showDetails.value
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.measurement-card {
  width: calc(50% - 8px);
  height: fit-content;

  .value {
    text-align: center;
    padding: 24px 0;
  }

  .footer-container {
    width: 100%;
  }

  .button-group {
    display: flex;
    width: 100%;
    justify-content: space-around;
    margin-bottom: 4px;
  }

  &.fullscreen {
    width: calc(25% - 12px);
  }
}

@media screen and (max-width: 1080px) {
  .measurement-card {
    &.fullscreen {
      width: calc(50% - 8px);
    }
  }
}

@media screen and (max-width: 580px) {
  .measurement-card {
    &.fullscreen {
      width: calc(100%);
    }
  }
}

@media screen and (max-width: 400px) {
  .measurement-card {
    width: 100%;
  }
}
</style>
